// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable formatjs/no-literal-string-in-jsx */
import { PageLayout } from "#/features/global-ui/page-layout"
import { Title } from "#/services/page-title/Title"
import { Paths } from "#/utils/Paths"
import { BookingFlow, useTelemedicineAccess } from "@internal/telemedicine"
import { useFlags } from "launchdarkly-react-client-sdk"
import React from "react"
import { Redirect, useHistory, useLocation } from "react-router-dom"

// The raw `?program=` value, or null when absent. Not validated here — the backend decides which
// programs the member may book (see useTelemedicineAccess).
function useProgramParam(): string | null {
  const { search } = useLocation()
  const raw = new URLSearchParams(search).get("program")
  return raw === null || raw === "" ? null : raw
}

export function TelemedicineBookingPage(): JSX.Element | null {
  const { telemedicineBookingFlow } = useFlags()
  const history = useHistory()
  const program = useProgramParam()
  const { data: access, isLoading, isError } = useTelemedicineAccess()

  if (!telemedicineBookingFlow) {
    return <Redirect to={Paths.HOME} />
  }

  // No program named → let the resolver decide.
  if (program === null) {
    return <Redirect to={Paths.TELEMEDICINE} />
  }

  // A failed access read is anomalous at this entry point — send the member home rather than render a
  // blank page or the flow (MEM-781). The BFF is the authority on booking either way.
  if (isError) {
    return <Redirect to={Paths.HOME} />
  }

  if (isLoading || !access) {
    return null
  }

  // Gate on the member's actual access: the requested program must be one their benefit turns on AND
  // one they're qualified for. Unqualified members no longer get a booking link at all (MEM-817), so
  // reaching this URL while unqualified — or for a program that isn't theirs — is anomalous; send
  // them home. This doesn't leak unknown-vs-off-benefit-vs-unqualified and doesn't reroute into a
  // different program. UX gate only — the BFF re-enforces every booking procedure server-side.
  const match = access.programs.find((candidate) => candidate.program === program)
  if (!match || !match.qualified) {
    return <Redirect to={Paths.HOME} />
  }

  return (
    <PageLayout variant="default">
      <Title title="Telemedicine Booking" />
      <BookingFlow
        program={match.program}
        onExit={() => history.push(Paths.HOME)}
        onReturnToProviderResults={() => history.push(Paths.PROVIDER_FINDER)}
        onExploreCareOptions={() => history.push(Paths.READ)}
      />
    </PageLayout>
  )
}
